import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getGeoZoneById,
  createGeoZone,
  updateGeoZone,
  getCountries,
  getCities,
} from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import { notify } from "../utils/notify";
import Select from "react-select";
import PageHeader from "../components/UI/PageHeader";
import EntityTranslationsButton from "../components/UI/EntityTranslationsButton";
import { useTranslation } from "../context/I18nContext";

// لا يوجد endpoint "يرجع كل المدن دفعة واحدة"، فبنطلب limit عالي كفاية
// لتغطية أغلب الاستخدامات الحالية. لو عدد المدن كبر أكتر من كده مستقبلاً
// لازم يتحول لـ async searchable select بدل تحميلها كلها.
const CITIES_OPTIONS_LIMIT = 1000;

export default function AddEditGeoZone() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("geo_zones");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [countryOptions, setCountryOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getCountries(token)
      .then((res) => setCountryOptions(res.map((c) => ({ value: c.id, label: c.name }))))
      .catch(() => notify.error(t("notify_load_countries_failed", "Failed to load countries")));

    getCities(token, { limit: CITIES_OPTIONS_LIMIT })
      .then((res) => setCityOptions((res.data || []).map((c) => ({ value: c.id, label: c.name }))))
      .catch(() => notify.error(t("notify_load_cities_failed", "Failed to load cities")));

    if (id) {
      getGeoZoneById(token, id).then((gz) => {
        setName(gz.name || "");
        setDescription(gz.description || "");
        setSelectedCountries((gz.countries || []).map((c) => ({ value: c.id, label: c.name })));
        setSelectedCities((gz.cities || []).map((c) => ({ value: c.id, label: c.name })));
      }).catch(() => notify.error(t("notify_load_failed", "Failed to load geo zone")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    setErrors({});

    if (!name.trim()) {
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      setErrors({ name: t("validation_name_required", "GeoZone name is required") });
      return;
    }

    setLoading(true);
    const countryIds = selectedCountries.map((c) => c.value);
    const cityIds = selectedCities.map((c) => c.value);

    try {
      if (id) {
        // Update يقبل التعديل على الاسم وعلاقات الدول/المدن دفعة واحدة
        await updateGeoZone(token, id, {
          name: name.trim(),
          country_ids: countryIds,
          city_ids: cityIds,
        });
        notify.success(t("notify_updated", "GeoZone updated successfully"));
      } else {
        // Create بينشئ السجل الأساسي بس (الاسم والوصف)، وبعدها بنربط
        // الدول/المدن بنفس استدعاء الـ Update عشان الـ API الحالي مش بيقبل
        // country_ids/city_ids وقت الإنشاء مباشرة.
        const created = await createGeoZone(token, { name: name.trim(), description: description.trim() });
        if (countryIds.length > 0 || cityIds.length > 0) {
          await updateGeoZone(token, created.id, { country_ids: countryIds, city_ids: cityIds });
        }
        notify.success(t("notify_created", "GeoZone created successfully"));
      }
      navigate("/geozones");
    } catch (err) {
      const errorMsg = err.data?.error || err.message || tCommon("error_generic", "Something went wrong");
      if (errorMsg.toLowerCase().includes("name")) {
        setErrors({ name: errorMsg });
      }
      notify.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      padding: '2px',
      borderRadius: '12px',
      borderColor: '#f3f4f6',
      backgroundColor: '#f9fafb80',
      '&:hover': { borderColor: 'var(--primary-color)' },
      boxShadow: 'none',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: 'var(--primary-color)',
      color: 'white',
      borderRadius: '8px',
    }),
    multiValueLabel: (base) => ({ ...base, color: 'white' }),
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/geozones")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">
            {id ? t("edit_title", "Edit GeoZone") : t("add_title", "Create New GeoZone")}
          </h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "Group countries and cities into a custom zone")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-8">
          <div className="flex items-end gap-3">
            <div className="flex-grow">
              <FloatingInput
                label={t("field_name_label", "GeoZone Name")}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors({});
                }}
                error={errors.name}
                icon={ICONS.GeoZones}
              />
            </div>
            <EntityTranslationsButton entityType="geo_zone" entityId={id} defaultName={name} />
          </div>

          {!id && (
            <FloatingInput
              label={t("field_description_label", "Description")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}

          <div className="space-y-3">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ms-4">
              {t("countries_label", "Countries")}
            </label>
            <Select
              options={countryOptions}
              value={selectedCountries}
              onChange={setSelectedCountries}
              isMulti
              placeholder={t("countries_select_placeholder", "Select countries covered by this zone...")}
              styles={customSelectStyles}
              className="text-sm"
            />
          </div>

          <div className="space-y-3">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ms-4">
              {t("cities_label", "Cities (optional overrides)")}
            </label>
            <Select
              options={cityOptions}
              value={selectedCities}
              onChange={setSelectedCities}
              isMulti
              placeholder={t("cities_select_placeholder", "Select specific cities...")}
              styles={customSelectStyles}
              className="text-sm"
            />
            <p className="text-[10px] text-gray-400 ms-4 italic">
              {t("cities_hint", "* Use this to add specific cities in addition to (or instead of) whole countries.")}
            </p>
          </div>

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_geo_zone", "Update GeoZone") : t("btn_create_geo_zone", "Create GeoZone")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/geozones")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
